import { useState, useEffect, useCallback, useMemo } from 'react';
import { analyticsAPI } from '../../../services/analytics';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CITY_ORDER_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];
const CITY_USER_COLORS = ['#A29BFE', '#FD79A8', '#FDCB6E', '#6C5CE7', '#00CEC9'];

const buildCityOrderData = (orders) => {
  const cityData = {};
  orders.forEach(order => {
    const city = (order.location || 'Unknown').toLowerCase();
    if (!cityData[city]) cityData[city] = { count: 0, orders: [] };
    cityData[city].count++;
    cityData[city].orders.push(order);
  });
  return cityData;
};

const buildCityUserData = (monthlyData) => {
  const cityUsersData = {};
  monthlyData.forEach(monthData => {
    (monthData.users || []).forEach(user => {
      const city = (user.location || 'Unknown').toLowerCase();
      if (!cityUsersData[city]) cityUsersData[city] = {};
      if (user._id) cityUsersData[city][user._id] = user;
    });
  });
  return cityUsersData;
};

const makeCityChart = (cityData, label, colors) => ({
  labels: Object.keys(cityData),
  datasets: [{
    label,
    data: Object.values(cityData).map(c => typeof c === 'object' && 'count' in c ? c.count : Object.keys(c).length),
    backgroundColor: colors,
    borderWidth: 0
  }]
});

const initialState = {
  stats: { delivered: 0, cancelled: 0, running: 0, upi: 0, cod: 0, totalRevenue: 0, totalUsers: 0 },
  statusChart: null,
  paymentChart: null,
  monthlyChart: null,
  cityChart: null,
  cityUsersChart: null,
  revenueComparisonChart: null,
  userComparisonChart: null,
  cityOrdersData: {},
  cityUsersRawData: {},
  userMonthlyData: [],
  dayWiseUserData: [],
  monthlyOrderIds: []
};

export const useAnalyticsData = (filters) => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(initialState);

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filterKey]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      if (debouncedFilters.startDate && debouncedFilters.endDate) {
        const [mainData, prevYearData, userData, deliveredData, cancelledData, runningData, upiData, codData] = await Promise.all([
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate),
          analyticsAPI.getOrdersByYear(debouncedFilters.compareYear),
          analyticsAPI.getUsersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate),
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate, 'Delivered'),
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate, 'Cancelled'),
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate, 'running_order'),
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate, null, 'UPI'),
          analyticsAPI.getOrdersByDateRange(debouncedFilters.startDate, debouncedFilters.endDate, null, 'COD')
        ]);

        const data = mainData.data;
        const cancelledRevenue = cancelledData.data.total_revenue || 0;
        const totalRevenue = (data.total_revenue || 0) - cancelledRevenue;
        const monthlyData = data.monthly_data || [];
        const allUsers = userData.data.users || [];
        const allOrders = data.orders || [];

        const cityData = buildCityOrderData(allOrders);

        // Build city users from date-range user data
        const cityUsersData = {};
        allUsers.forEach(user => {
          const city = (user.location || 'Unknown').toLowerCase();
          if (!cityUsersData[city]) cityUsersData[city] = {};
          if (user._id) cityUsersData[city][user._id] = user;
        });

        // Build day-wise user data
        const start = new Date(debouncedFilters.startDate);
        const end = new Date(debouncedFilters.endDate);
        const dateRangeLabels = [];
        const dateRangeData = [];
        const dayWiseUserData = [];
        const cur = new Date(start);

        // Pre-group users by date string for O(n) lookup
        const usersByDate = {};
        allUsers.forEach(user => {
          const key = new Date(user.createdAt).toDateString();
          if (!usersByDate[key]) usersByDate[key] = [];
          usersByDate[key].push(user);
        });

        while (cur <= end) {
          const dayLabel = cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const dayUsers = usersByDate[cur.toDateString()] || [];
          dateRangeLabels.push(dayLabel);
          dateRangeData.push(dayUsers.length);
          dayWiseUserData.push({ day: dayLabel, users: dayUsers });
          cur.setDate(cur.getDate() + 1);
        }

        setDashboardData({
          stats: {
            delivered: deliveredData.data.total_orders || 0,
            cancelled: cancelledData.data.total_orders || 0,
            running: runningData.data.total_orders || 0,
            upi: upiData.data.total_orders || 0,
            cod: codData.data.total_orders || 0,
            totalRevenue,
            totalUsers: allUsers.length
          },
          userMonthlyData: allUsers,
          dayWiseUserData,
          monthlyOrderIds: allOrders,
          cityOrdersData: cityData,
          cityUsersRawData: cityUsersData,
          statusChart: {
            labels: ['Delivered', 'Running', 'Cancelled'],
            datasets: [{
              data: [deliveredData.data.total_orders || 0, runningData.data.total_orders || 0, cancelledData.data.total_orders || 0],
              backgroundColor: ['#00B894', '#FFEAA7', '#FF6B6B'],
              borderWidth: 0
            }]
          },
          paymentChart: {
            labels: ['UPI', 'COD'],
            datasets: [{
              data: [upiData.data.total_orders || 0, codData.data.total_orders || 0],
              backgroundColor: ['#96CEB4', '#FFEAA7'],
              borderWidth: 0
            }]
          },
          cityChart: makeCityChart(cityData, 'Orders', CITY_ORDER_COLORS),
          cityUsersChart: {
            labels: Object.keys(cityUsersData),
            datasets: [{
              label: 'Users',
              data: Object.values(cityUsersData).map(c => Object.keys(c).length),
              backgroundColor: CITY_USER_COLORS,
              borderWidth: 0
            }]
          },
          monthlyChart: {
            labels: monthlyData.length > 0 ? monthlyData.map(m => m.month?.split(' ')[0] || 'N/A') : MONTHS,
            datasets: [
              {
                label: 'Orders',
                data: monthlyData.length > 0 ? monthlyData.map(m => m.count || 0) : new Array(12).fill(0),
                borderColor: '#F97316',
                backgroundColor: '#F97316',
                yAxisID: 'y'
              },
              {
                label: 'Revenue (₹)',
                data: monthlyData.length > 0 ? monthlyData.map(m => m.revenue || 0) : new Array(12).fill(0),
                borderColor: '#8B5CF6',
                backgroundColor: '#8B5CF6',
                yAxisID: 'y1'
              }
            ]
          },
          revenueComparisonChart: {
            labels: MONTHS,
            datasets: [
              {
                label: `${debouncedFilters.year} Revenue (₹)`,
                data: monthlyData.map(m => m.revenue || 0),
                borderColor: '#22C55E',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.4
              },
              {
                label: `${debouncedFilters.compareYear} Revenue (₹)`,
                data: prevYearData.data.monthly_data?.map(m => m.revenue) || new Array(12).fill(0),
                borderColor: '#F97316',
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                tension: 0.4
              }
            ]
          },
          userComparisonChart: {
            labels: dateRangeLabels,
            datasets: [{
              label: `${debouncedFilters.startDate} to ${debouncedFilters.endDate}`,
              data: dateRangeData,
              borderColor: '#00CEC9',
              backgroundColor: 'rgba(0, 206, 201, 0.2)',
              tension: 0.4,
              fill: true
            }]
          }
        });
        return;
      }

      const [delivered, cancelled, running, upi, cod, currentYearData, previousYearData, userData, compareUserData, yearOrdersData] = await Promise.all([
        analyticsAPI.getOrdersByStatus('Delivered', debouncedFilters.year),
        analyticsAPI.getOrdersByStatus('Cancelled', debouncedFilters.year),
        analyticsAPI.getOrdersByStatus('running_order', debouncedFilters.year),
        analyticsAPI.getOrdersByPaymentMode('UPI', debouncedFilters.year),
        analyticsAPI.getOrdersByPaymentMode('COD', debouncedFilters.year),
        analyticsAPI.getOrdersByYear(debouncedFilters.year),
        analyticsAPI.getOrdersByYear(debouncedFilters.compareYear),
        analyticsAPI.getUsersByYear(debouncedFilters.year),
        analyticsAPI.getUsersByYear(debouncedFilters.compareYear),
        analyticsAPI.getOrdersByDateRange(`${debouncedFilters.year}-01-01`, `${debouncedFilters.year}-12-31`)
      ]);

      const userGraphData = userData.data.monthly_data || [];
      const compareUserGraphData = compareUserData.data.monthly_data || [];
      const cityData = buildCityOrderData(yearOrdersData.data.orders || []);
      const cityUsersData = buildCityUserData(userGraphData);

      setDashboardData({
        stats: {
          delivered: delivered.data.total_orders || 0,
          cancelled: cancelled.data.total_orders || 0,
          running: running.data.total_orders || 0,
          upi: upi.data.total_orders || 0,
          cod: cod.data.total_orders || 0,
          totalRevenue: (currentYearData.data.total_revenue || 0) - (cancelled.data.total_revenue || 0),
          totalUsers: userData.data.total_users || 0
        },
        userMonthlyData: userGraphData,
        compareUserMonthlyData: compareUserGraphData,
        monthlyOrderIds: currentYearData.data.orders || [],
        cityOrdersData: cityData,
        cityUsersRawData: cityUsersData,
        statusChart: {
          labels: ['Delivered', 'Running', 'Cancelled'],
          datasets: [{
            data: [delivered.data.total_orders || 0, running.data.total_orders || 0, cancelled.data.total_orders || 0],
            backgroundColor: ['#00B894', '#FFEAA7', '#FF6B6B'],
            borderWidth: 0
          }]
        },
        paymentChart: {
          labels: ['UPI', 'COD'],
          datasets: [{
            data: [upi.data.total_orders || 0, cod.data.total_orders || 0],
            backgroundColor: ['#96CEB4', '#FFEAA7'],
            borderWidth: 0
          }]
        },
        cityChart: makeCityChart(cityData, 'Orders', CITY_ORDER_COLORS),
        cityUsersChart: {
          labels: Object.keys(cityUsersData),
          datasets: [{
            label: 'Users',
            data: Object.values(cityUsersData).map(c => Object.keys(c).length),
            backgroundColor: CITY_USER_COLORS,
            borderWidth: 0
          }]
        },
        monthlyChart: {
          labels: MONTHS,
          datasets: [
            {
              label: 'Orders',
              data: currentYearData.data.monthly_data?.map(m => m.count) || new Array(12).fill(0),
              borderColor: '#E17055',
              backgroundColor: '#E17055',
              yAxisID: 'y'
            },
            {
              label: 'Revenue (₹)',
              data: currentYearData.data.monthly_data?.map(m => m.revenue) || new Array(12).fill(0),
              borderColor: '#00B894',
              backgroundColor: '#00B894',
              yAxisID: 'y1'
            }
          ]
        },
        revenueComparisonChart: {
          labels: MONTHS,
          datasets: [
            {
              label: `${debouncedFilters.year} Revenue (₹)`,
              data: currentYearData.data.monthly_data?.map(m => m.revenue) || new Array(12).fill(0),
              borderColor: '#6C5CE7',
              backgroundColor: 'rgba(108, 92, 231, 0.2)',
              tension: 0.4,
              fill: true
            },
            {
              label: `${debouncedFilters.compareYear} Revenue (₹)`,
              data: previousYearData.data.monthly_data?.map(m => m.revenue) || new Array(12).fill(0),
              borderColor: '#FD79A8',
              backgroundColor: 'rgba(253, 121, 168, 0.2)',
              tension: 0.4,
              fill: true
            }
          ]
        },
        userComparisonChart: {
          labels: MONTHS,
          datasets: [
            {
              label: `${debouncedFilters.year} Users`,
              data: userGraphData.map(m => m.count || 0),
              borderColor: '#00CEC9',
              backgroundColor: 'rgba(0, 206, 201, 0.2)',
              tension: 0.4,
              fill: true
            },
            {
              label: `${debouncedFilters.compareYear} Users`,
              data: compareUserGraphData.map(m => m.count || 0),
              borderColor: '#FDCB6E',
              backgroundColor: 'rgba(253, 203, 110, 0.2)',
              tension: 0.4,
              fill: true
            }
          ]
        }
      });
    } catch (error) {
      // silently handle fetch errors
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return { loading, dashboardData };
};
